/**
 * The physical tray: 60 wells in twelve blocks of five, two blocks per legacy group (D-012). The results screen
 * mirrors the paper record, whose columns pair up A+D, B+E and C+F (design-critique.md C-42).
 */
export const WELLS = 60;
export const BLOCK_SIZE = 5;
export const BLOCKS = WELLS / BLOCK_SIZE;
export const GROUP_SIZE = 10;

export const GROUP_LABELS: Record<number, string> = { 1: "Trees A", 2: "Trees B", 3: "Grasses", 4: "Weeds", 5: "Animals", 6: "Environmental" };

/** 1-based block (1 to 12) of a 1-based tray position. */
export const blockOf = (position: number): number => Math.ceil(position / BLOCK_SIZE);
/** Legacy group (1 to 6) of a 1-based tray position. */
export const groupOf = (position: number): number => Math.ceil(position / GROUP_SIZE);
/** Environmental antigens (group 6) never mix with other groups in an order. */
export const isEnvironmental = (position: number): boolean => groupOf(position) === 6;

/** The paper's column pairs, by group: A+D, B+E, C+F. */
export const PAPER_PAIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 4],
  [2, 5],
  [3, 6],
];

/** Positions sorted and split into the twelve blocks of five, in tray order. */
export function trayBlocks<T extends { position: number }>(positions: readonly T[]): T[][] {
  const sorted = [...positions].sort((a, b) => a.position - b.position);
  const blocks: T[][] = Array.from({ length: BLOCKS }, () => []);
  for (const p of sorted) {
    const b = blockOf(p.position) - 1;
    if (b >= 0 && b < BLOCKS) blocks[b]!.push(p);
  }
  return blocks;
}

/** The paper layout: three rows, each a pair of groups, each group its ten positions in order. */
export function paperRows<T extends { position: number }>(positions: readonly T[]): Array<{ left: { group: number; items: T[] }; right: { group: number; items: T[] } }> {
  const sorted = [...positions].sort((a, b) => a.position - b.position);
  const byGroup = (g: number) => sorted.filter((p) => groupOf(p.position) === g);
  return PAPER_PAIRS.map(([l, r]) => ({ left: { group: l, items: byGroup(l) }, right: { group: r, items: byGroup(r) } }));
}

/** A reading typed into a well: empty is "not read" (Q-09), otherwise a whole number of millimetres 0 to 21. */
export function parseReading(text: string): number | null | "invalid" {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d{1,2}$/.test(t)) return "invalid";
  const n = Number(t);
  return n >= 0 && n <= 21 ? n : "invalid";
}
